import { RunQueryFor } from "./runQueryFn";
import { ApiBuilder } from "./apiBuilder";
import { queryOptions } from "@tanstack/react-query";
import { EXTENDED_STALE_TIME_MS } from "./queryClient";

/**
 * All queries that are related to the payment should have this tag.
 * It should be used to invalidate each query after the payment is done.
 */
export const PAYMENT_INVALIDATION_TAG = "payment";

/**
 * Represents the example of configuration for the 'documents' query.
 */
export const documentsQueryConfig = (alias: string) => ({
  queryKey: ["documents", alias],
  queryFn: ({ queryKey }: { queryKey: string[] }) =>
    RunQueryFor<{ queryType: string }>(ApiBuilder.queries.documents.byAlias(queryKey[1]).url),
});

export interface ICardsIndexResponse {
  paymentInstrumentId: number;
  expirationMonth: number;
  expirationYear: number;
  cardNumber: string;
  cardHolderName: string;
  aliasName: string;
  cardBrand: string;
  processDate: string;
}

export const cardsQueryConfig = () => ({
  queryKey: ["cards"],
  queryFn: () => RunQueryFor<ICardsIndexResponse[]>(ApiBuilder.queries.cards.index),
  // We don't need to retry this query quite often. It's enough to invalidate a cache
  // manually when a new card is added or removed.
  staleTime: 1000 * 60 * 10, // 10 min in ms
});

type operationsQueryConfigParams = Parameters<typeof ApiBuilder.queries.operations.index>[0];
const operationsQueryDefaultParams: Partial<operationsQueryConfigParams> = {
  startDate: "2019-01-01",
  perPage: 100,
  page: 0,
};

export interface IOperationsQueryResponse {
  perPage: number;
  page: number;
  data: {
    id: number;
    createDate: string;
    merchantName: string;
    amount: number;
    currencyCode: "RUB";
    operationStatus: number;
    termInfo: {
      value: string;
      unit: string;
    };
    dateNextPayment: string;
    macrocategory: string;
    smallIconLink: string;
  }[];
}

export const operationsQueryConfig = (params: Partial<operationsQueryConfigParams> = {}) => {
  const withParams = {
    endDate: new Date().toISOString().match(/^[^T]+/i)![0],
    ...operationsQueryDefaultParams,
    ...params,
  } as operationsQueryConfigParams;
  return {
    queryKey: ["operations", withParams] as [string, operationsQueryConfigParams],
    queryFn: ({ queryKey }: { queryKey: [string, operationsQueryConfigParams] }) =>
      RunQueryFor<IOperationsQueryResponse>(ApiBuilder.queries.operations.index(queryKey[1])),
    staleTime: EXTENDED_STALE_TIME_MS,
  };
};

export interface IRepaymentScheduleByIdResponse {
  number: number;
  date: string;
  amount: number;
  currencyCode: "RUB";
  status: number;
  actualPaymentAmount: number;
}

export const repaymentSchedulesByIdQueryConfig = (id?: number) => queryOptions({
  queryKey: [PAYMENT_INVALIDATION_TAG, "repaymentSchedules", id!],
  queryFn: ({ queryKey }) =>
    RunQueryFor<IRepaymentScheduleByIdResponse[]>(
      ApiBuilder.queries.operations.byId(queryKey[2].toString()).repaymentSchedule,
    ),
  enabled: !!id,
  staleTime: EXTENDED_STALE_TIME_MS,
});

export interface IOperationByIdResponse {
  operation: {
    number: string;
    createDate: string;
    merchantName: string;
    amount: number;
    overdueAmount: number;
    remainingAmount: number;
    currencyCode: "RUB";
    macrocategory: string;
    operationStatus: number;
    smallIconLink: string;
    termInfo: {
      value: string;
      unit: string;
    };
  };
}

export const operationByIdQueryConfig = (id: string) => queryOptions({
  queryKey: [PAYMENT_INVALIDATION_TAG, "operation", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<IOperationByIdResponse>(
      ApiBuilder.queries.operations.byId(queryKey[2].toString()).index,
    ),
});

export interface IOperationCommissionResponse {
  currencyCode: "RUB";
  amount: number;
}

export const operationCommissionQueryConfig = (id: string) => queryOptions({
  queryKey: ["operationCommission", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<IOperationCommissionResponse>(
      ApiBuilder.queries.operations.byId(queryKey[1].toString()).commission,
    ),
  staleTime: Infinity,
});

export interface IOperationProductsResponse {
  items: {
    name: string;
    amount: number;
    currencyCode: "RUB";
  }[];
  totalAmount: {
    amount: number;
    currencyCode: "RUB";
  };
}

export const productsQueryConfig = (id: string) => queryOptions({
  queryKey: ["products", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<IOperationProductsResponse>(
      ApiBuilder.queries.operations.byId(queryKey[1].toString()).products,
    ),
  staleTime: EXTENDED_STALE_TIME_MS,
});

export const cardBindingLinkQueryConfig = () => queryOptions({
  queryKey: ["cardBindingLink"],
  queryFn: () =>
    RunQueryFor<{ url: string }>({
      ...ApiBuilder.queries.cards.binding.link,
      query: {
        successCallbackUrl: `${window.location.origin}/card/connected`,
        errorCallbackUrl: `${window.location.origin}/card/error`,
      },
    }),
  staleTime: 0,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchInterval: false,
  refetchOnWindowFocus: false,
});

import { VITE_PARTNER_ID } from "../ENV";

type limitsQueryConfigParams = Parameters<typeof ApiBuilder.queries.limits.index>[0];
const limitsQueryDefaultParams: Partial<limitsQueryConfigParams> = {
  count: 100,
  page: 0,
  partnerIds: VITE_PARTNER_ID ? [VITE_PARTNER_ID] : undefined,
};
export interface ILimitResponse {
  id: string;
  name: string;
  totalAmount: number;
  availableAmount: number;
  currencyCode: string;
  status: string;
}
export const limitsQueryConfig = (params: Partial<limitsQueryConfigParams> = {}) => {
  const withParams = {
    ...limitsQueryDefaultParams,
    ...params,
    partnerIds: params.partnerIds ?? limitsQueryDefaultParams.partnerIds,
  } as limitsQueryConfigParams;
  return {
    queryKey: ["limits", withParams] as [string, limitsQueryConfigParams],
    queryFn: ({ queryKey }: { queryKey: [string, limitsQueryConfigParams] }) =>
      RunQueryFor<ILimitResponse[]>(ApiBuilder.queries.limits.index(queryKey[1])),
    staleTime: 1000 * 60 * 10, // 10 min in ms
    retry: false,
    select: (data: ILimitResponse[]) => data.length > 0 ? [data[0]] : [],
  };
};

export const limitByIdQueryConfig = (id: string) => queryOptions({
  queryKey: ["limit", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<ILimitResponse>(
      ApiBuilder.queries.limits.byId(queryKey[1].toString()).index,
    ),
});

export interface ILimitPaymentSchedule {
  items: {
    date: string;
    number: number;
    plannedFeeAmount: number;
    plannedPaymentAmount: number;
    plannedPrincipalAmount: number;
    remainingFeeAmount: number;
    remainingPaymentAmount: number;
    remainingPrincipalAmount: number;
    status: string; // PAID | PLANNED | OVERDUE
  }[];
  currencyCode: "RUB";
  totalPlannedFeeAmount: number;
  totalPlannedPaymentAmount: number;
  totalPlannedPrincipalAmount: number;
  totalRemainingFeeAmount: number;
  totalRemainingPaymentAmount: number;
  totalRemainingPrincipalAmount: number;
}

export const limitPaymentScheduleByIdQueryConfig = (id: string) => queryOptions({
  queryKey: ["paymentSchedule", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<ILimitPaymentSchedule>(
      ApiBuilder.queries.limits.byId(queryKey[1].toString()).paymentSchedule,
    ),
});

export type TPaymentCardResponse = "NEW" | "ACTIVE" | "PENDING" | "ERROR" | "OTP_REQUIRED" | "OTP_EXPECTED" | "WAITING_FOR_ACTIVATION";
export const cardIssuanceStatusQueryConfig = (id: string) => queryOptions({
  queryKey: ["cardIssuance", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<TPaymentCardResponse>(
      ApiBuilder.queries.limits.byId(queryKey[1].toString()).cardIssuanceStatus,
    ),
  staleTime: EXTENDED_STALE_TIME_MS,
  retry: false,
});

export type TPayoutStatus = "PENDING" | "SUCCESS" | "ERROR";
export interface IPayoutStatusResponse {
  status: TPayoutStatus;
}

export const payoutStatusQueryConfig = (payoutId: string) => queryOptions({
  queryKey: ["payout", payoutId],
  queryFn: ({ queryKey }) =>
    RunQueryFor<IPayoutStatusResponse>(
      ApiBuilder.queries.payout.byId(queryKey[1].toString()).status,
    ),
  staleTime: EXTENDED_STALE_TIME_MS,
  retry: false,
});

export type TPaymentType = "LIMIT" | "NOT_LIMIT";
export interface IUpcomingPayment {
  date: string;
  name: string;
  amount: number;
  currencyCode: "RUB";
  isOverdue: boolean;
  type: TPaymentType;
  id: string;
}

export const upcomingPaymentsQueryConfig = () => queryOptions({
  queryKey: ["upcomingPayments"],
  queryFn: () => RunQueryFor<IUpcomingPayment[]>(ApiBuilder.queries.payments.upcoming),
  staleTime: 1000 * 60 * 10, // 10 min in ms
});

export const paymentCardAvailabilityQueryConfig = (id: string) => queryOptions({
  queryKey: ["paymentCardAvailability", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<boolean>(ApiBuilder.queries.limits.byId(queryKey[1].toString()).paymentCardAvailability),
  // staleTime: EXTENDED_STALE_TIME_MS,
  staleTime: 1000 * 60 * 10, // 10 min in ms
});

export const cardRequisitesQueryConfig = (id: string) => queryOptions({
  queryKey: ["paymentCardRequisites", id],
  queryFn: ({ queryKey }) =>
    RunQueryFor<{ url: string }>(ApiBuilder.queries.limits.byId(queryKey[1].toString()).cardRequisites),
});

export interface ILimitPaymentCard {
  id: string;
  limitId: string;
}

export const LIMIT_PAYMENT_CARDS_INVALIDATION_TAG = "limitPaymentCards";

export const limitPaymentCards = () => queryOptions({
  queryKey: [LIMIT_PAYMENT_CARDS_INVALIDATION_TAG],
  queryFn: () => RunQueryFor<ILimitPaymentCard[]>(ApiBuilder.queries.paymentCards.index),
  staleTime: 1000 * 60 * 10, // 10 min in ms
});
